/**
 * Camp-pickup menu — the ⋯ trigger + portaled menu + seam adapter (v1 T2).
 *
 * Maps each pure intent from buildCampMenuItems to its seam:
 * global seams via PersonaChatUIContext / a null-guarded CouncilContext,
 * in-node actions (set_goal, peek_brief) via props the node supplies.
 * The menu portals to document.body to escape the node's clipping/
 * stacking context (the fixed primitive positions fine un-portaled).
 * strike_camp is a 2-step confirm rendered as its own portal panel —
 * UnifiedContextMenu's MenuItem can't carry a testid and disabled items
 * have no onClick path. The confirm intentionally does NOT close on
 * outside-click: a destructive confirm should be an explicit choice;
 * Escape and "Keep them on the trail" are the exits.
 */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UnifiedContextMenu, MenuItem } from '../../../ui/UnifiedContextMenu';
import { PersonaChatUIContext } from '../../../../contexts/PersonaChatUIContext';
import { CouncilContext } from '../../../../contexts/CouncilContext';
import { LensContext } from '../../../../contexts/LensContext';
import { forkFocusLens } from '../../forkLensDrive';
import { buildCampMenuItems } from './buildCampMenuItems';
import type { CampMenuContext } from './types';

export function CampMenu({ context, onSetGoal, onPeekBrief }: {
  context: CampMenuContext;
  onSetGoal: () => void;
  onPeekBrief: () => void;
}) {
  const ui = useContext(PersonaChatUIContext);
  const council = useContext(CouncilContext); // null on non-council renders → strike disabled
  const lens = useContext(LensContext);       // null on non-lens surfaces → lens intents disabled
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [confirmStrike, setConfirmStrike] = useState(false);

  const close = () => { setPos(null); setConfirmStrike(false); btnRef.current?.focus(); };

  useEffect(() => {
    if (!pos) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pos]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (e: React.MouseEvent) => {
    e.stopPropagation();
    const r = btnRef.current?.getBoundingClientRect();
    setPos(r ? { x: r.left, y: r.bottom + 4 } : { x: e.clientX, y: e.clientY });
    setConfirmStrike(false);
  };

  const items: MenuItem[] = [];
  let lastGroup = '';
  for (const it of buildCampMenuItems(context)) {
    if (it.group !== lastGroup) { items.push({ type: 'header', label: it.group === 'focus' ? 'FOCUS' : 'MANAGE' }); lastGroup = it.group; }
    const disabled = it.disabled
      || (it.id === 'strike_camp' && !council)
      || ((it.id === 'focus_persona' || it.id === 'pin_trail') && !lens)
      || (it.id === 'new_path' && !lens)
      || (it.id === 're_roll' && !ui?.forkReroll);
    items.push({
      label: it.label,
      danger: it.danger,
      disabled,
      icon: it.id === 'fold_back' ? '↩' : undefined,
      onClick: disabled ? undefined : () => {
        switch (it.id) {
          case 'fold_back': ui?.openFoldBack(context.spineRootMessageId); close(); break;
          // find soft-fails to a toast when the bubble isn't in the DOM — accepted v1 contract
          case 'find': ui?.focusBubble(context.spineRootMessageId); close(); break;
          case 'set_goal': onSetGoal(); close(); break;
          case 'peek_brief': onPeekBrief(); close(); break;
          // P3 SP2 — map drives the lens. setLens normalizes internally
          // (SP1 delta 5): pin derives trail mode + clears the persona set.
          // lens!/context.trailId! are safe: the arm is unreachable when
          // disabled stripped onClick (null lens / null trailId).
          case 'focus_persona':
            lens!.setLens({ ...forkFocusLens(context.personaId), activeTrailId: null });
            close();
            break;
          case 'pin_trail':
            lens!.setLens({ activeTrailId: context.trailId });
            close();
            break;
          case 'strike_camp': setConfirmStrike(true); break; // arm: swap the menu for the confirm panel
          // Shared spines P2 — trail-join via the gated propose path.
          // context.trailId! safe: disabled (trailId==null) strips onClick.
          case 'invite_here': ui?.openInviteToTrail(context.trailId!); close(); break;
          // §3 mid-spine fork (camp-root). new_path enters fork mode on the camp root
          // (M = spineRootMessageId) → the compose bar shows the fork pill. re_roll fires
          // an immediate reroll via the shared ui seam (F8). Both safe: disabled strips
          // onClick when lens / forkReroll is absent.
          case 'new_path':
            lens!.setLens({ cursorTurnId: context.spineRootMessageId, cursorMode: 'fork' });
            close();
            break;
          case 're_roll':
            ui?.forkReroll?.(context.spineRootMessageId, context.personaId, context.personaName);
            close();
            break;
          default: break;
        }
      },
    });
  }

  if (!ui?.canFoldBack) return null; // Trailhead surface only (same gate as the other camp affordances)
  return (
    <>
      <button ref={btnRef} type="button" data-testid={`camp-menu-trigger-${context.personaId}`}
        onClick={open} title="Camp actions"
        aria-haspopup="menu" aria-expanded={!!pos} aria-label={`Camp actions for ${context.personaName}`}
        className="nodrag ml-1 text-[11px] px-1 rounded text-gray-400 hover:bg-gray-200/60 dark:hover:bg-gray-700/40">
        <span aria-hidden>⛗</span>
      </button>
      {pos && !confirmStrike && createPortal(
        <div data-testid="camp-menu-open">
          <UnifiedContextMenu x={pos.x} y={pos.y} items={items} onClose={close} />
        </div>, document.body)}
      {pos && confirmStrike && createPortal(
        <div data-testid="camp-menu-strike-open" role="alertdialog" aria-label={`Strike ${context.personaName}'s camp?`}
          style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 9999 }}
          className="w-64 rounded-md border shadow-lg p-3 text-xs bg-white border-gray-200 text-gray-800 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100">
          <div className="font-semibold mb-1">Strike {context.personaName}&rsquo;s camp?</div>
          <p className="opacity-80 mb-2">Send this companion off the trail — their camp and findings-in-progress go with them.</p>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setConfirmStrike(false)}
              className="px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
              Keep them on the trail
            </button>
            <button type="button" data-testid="camp-menu-strike-confirm"
              onClick={() => { council?.dismiss(context.personaId); close(); }}
              className="px-2 py-1 rounded font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600">
              Yes — strike camp
            </button>
          </div>
        </div>, document.body)}
    </>
  );
}
